import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaCloud, FaDatabase, FaBrain, FaLaptopCode } from 'react-icons/fa';
import { Link } from 'react-router-dom';
// import Footer from '../components/Footer'; // Uncomment if Footer exists

const careerPaths = [
  {
    title: 'Software Development',
    icon: <FaCode className="text-blue-400 text-4xl" />,
    specializations: [
      {
        name: 'Frontend Developer',
        jobRoles: ['React Developer', 'Angular Developer', 'UI/UX Engineer'],
        demand: '📈 High (2025)',
        competition: '⚔️ Moderate',
        skills: ['HTML', 'CSS', 'JavaScript', 'React.js', 'TypeScript'],
        tools: ['VS Code', 'Figma', 'Tailwind CSS', 'Webpack'],
        timeTaken: '⏳ 6-12 months',
        roadmap: 'HTML/CSS → JavaScript → React → State Management → Portfolio',
      },
      {
        name: 'Backend Developer',
        jobRoles: ['Node.js Developer', 'Django Developer', 'Spring Boot Engineer'],
        demand: '📈 Very High',
        competition: '⚔️ High',
        skills: ['Node.js', 'Django', 'Java', 'APIs', 'SQL'],
        tools: ['Postman', 'MongoDB', 'MySQL', 'Docker', 'Git'],
        timeTaken: '⏳ 9-15 months',
        roadmap: 'Programming Basics → Server-side Language → Databases → API Dev → Deployment',
      },
      {
        name: 'Full Stack Developer',
        jobRoles: ['MERN Stack Developer', 'MEAN Stack Developer', 'Java Full Stack Engineer'],
        demand: '📈 Very High',
        competition: '⚔️ High',
        skills: ['JavaScript', 'React', 'Node.js', 'MongoDB', 'REST APIs'],
        tools: ['VS Code', 'Postman', 'Docker', 'AWS', 'Git'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'Frontend Basics → Backend Basics → Database Integration → Full Stack Projects → Deployment',
      },
      {
        name: 'Game Developer',
        jobRoles: ['Unity Developer', 'Unreal Engine Developer', 'Game Designer'],
        demand: '📈 Moderate',
        competition: '⚔️ High',
        skills: ['C#', 'C++', 'Unity', '3D Modeling', 'Game Physics'],
        tools: ['Unity', 'Unreal Engine', 'Blender', 'Photoshop', 'Git'],
        timeTaken: '⏳ 12-24 months',
        roadmap: 'Programming Basics → Game Engine → 2D Games → 3D Games → Multiplayer → Portfolio',
      },
      {
        name: 'DevOps Engineer',
        jobRoles: ['CI/CD Engineer', 'Infrastructure Engineer', 'Site Reliability Engineer'],
        demand: '📈 Very High',
        competition: '⚔️ Moderate',
        skills: ['Linux', 'Docker", "Kubernetes', 'CI/CD', 'AWS'],
        tools: ['Jenkins', 'Docker', 'Kubernetes', 'Terraform'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'Linux Basics → Scripting → Containers → CI/CD Pipelines → Cloud Platforms → Automation',
      },
    ],
  },
  {
    title: 'Data Science & Analytics',
    icon: <FaDatabase className="text-blue-400 text-4xl" />,
    specializations: [
      {
        name: 'Data Analyst',
        jobRoles: ['Business Analyst', 'Data Visualization Specialist'],
        demand: '📈 High',
        competition: '⚔️ Moderate',
        skills: ['SQL', 'Excel', 'Python', 'Statistics', 'Tableau'],
        tools: ['Power BI', 'Excel', 'SQL Server'],
        timeTaken: '⏳ 6-12 months',
        roadmap: 'Excel Basics → SQL → Visualization Tools → Real-world Analysis',
      },
      {
        name: 'Data Scientist',
        jobRoles: ['ML Engineer', 'AI Researcher'],
        demand: '📈 Very High',
        competition: '⚔️ High',
        skills: ['Python', 'Statistics', 'Pandas', 'ML', 'DL'],
        tools: ['TensorFlow', 'Jupyter', 'Scikit-learn'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'Python → Statistics → Data Wrangling → ML → Advanced Projects',
      },
      {
        name: 'Data Engineer',
        jobRoles: ['ETL Developer', 'Big Data Engineer', 'Data Pipeline Engineer'],
        demand: '📈 High',
        competition: '⚔️ Moderate',
        skills: ['SQL', 'Python', 'Hadoop', 'Spark', 'ETL'],
        tools: ['Airflow', 'Spark', 'Hadoop', 'AWS', 'Kafka'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'SQL → Python → Big Data Tools → Data Pipelines → Cloud Integration',
      },
      {
        name: 'Machine Learning Engineer',
        jobRoles: ['ML Model Developer', 'AI Engineer', 'Algorithm Engineer'],
        demand: '📈 Very High',
        competition: '⚔️ High',
        skills: ['Python', 'TensorFlow', 'PyTorch', 'ML Algorithms', 'Mathematics'],
        tools: ['Jupyter', 'TensorFlow', 'PyTorch', 'AWS', 'Git'],
        timeTaken: '⏳ 12-24 months',
        roadmap: 'Math Basics → Python → ML Fundamentals → Deep Learning → Production Deployment',
      },
    ],
  },
  {
    title: 'Cloud Computing',
    icon: <FaCloud className="text-blue-400 text-4xl" />,
    specializations: [
      {
        name: 'Cloud Solutions Architect',
        jobRoles: ['AWS Architect', 'Azure Architect', 'GCP Architect'],
        demand: '📈 Very High',
        competition: '⚔️ Moderate',
        skills: ['AWS', 'Azure', 'GCP', 'Architecture Design', 'Networking'],
        tools: ['AWS Console', 'Azure Portal', 'Terraform', 'Docker', 'Kubernetes'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'Cloud Basics → AWS/Azure/GCP → Architecture Patterns → Security → Certification',
      },
      {
        name: 'Cloud Engineer',
        jobRoles: ['Cloud Infrastructure Engineer', 'Cloud Support Engineer', 'Cloud Developer'],
        demand: '📈 High',
        competition: '⚔️ Moderate',
        skills: ['AWS', 'Linux', 'Scripting', 'Monitoring', 'CI/CD'],
        tools: ['AWS CLI', 'CloudWatch', 'Jenkins', 'Docker', 'Git'],
        timeTaken: '⏳ 9-15 months',
        roadmap: 'Linux → Cloud Fundamentals → Automation → Monitoring → Deployment',
      },
      {
        name: 'Cloud Security Engineer',
        jobRoles: ['Cloud Security Analyst', 'IAM Specialist', 'Compliance Engineer'],
        demand: '📈 Very High',
        competition: '⚔️ High',
        skills: ['Security', 'IAM', 'Encryption', 'AWS Security', 'Compliance'],
        tools: ['AWS IAM', 'Security Hub', 'Splunk', 'Firewall', 'VPN'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'Security Basics → Cloud Security → IAM → Monitoring → Certifications',
      },
      {
        name: 'Cloud DevOps Engineer',
        jobRoles: ['Cloud Automation Engineer', 'CI/CD Specialist', 'SRE'],
        demand: '📈 High',
        competition: '⚔️ Moderate',
        skills: ['Docker', 'Kubernetes', 'Terraform', 'CI/CD', 'AWS'],
        tools: ['Jenkins', 'Kubernetes', 'Terraform', 'AWS', 'Git'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'DevOps Basics → Containers → Cloud Platforms → Automation → Monitoring',
      },
      {
        name: 'Cloud Network Engineer',
        jobRoles: ['Cloud Network Architect', 'VPC Specialist', 'Connectivity Engineer'],
        demand: '📈 Moderate',
        competition: '⚔️ Moderate',
        skills: ['Networking', 'VPC', 'VPN', 'Load Balancing', 'AWS'],
        tools: ['AWS VPC', 'CloudFormation', 'Wireshark', 'Cisco', 'Git'],
        timeTaken: '⏳ 9-15 months',
        roadmap: 'Networking Basics → Cloud Networking → VPC Design → Security → Optimization',
      },
    ],
  },
  {
    title: 'Artificial Intelligence',
    icon: <FaBrain className="text-blue-400 text-4xl" />,
    specializations: [
      {
        name: 'Machine Learning Engineer',
        jobRoles: ['ML Model Developer', 'AI Engineer', 'Data Scientist'],
        demand: '📈 Very High',
        competition: '⚔️ High',
        skills: ['Python', 'TensorFlow', 'PyTorch', 'ML Algorithms', 'Mathematics'],
        tools: ['Jupyter', 'TensorFlow', 'PyTorch', 'AWS', 'Git'],
        timeTaken: '⏳ 12-24 months',
        roadmap: 'Math → Python → ML Basics → Advanced ML → Deployment',
      },
      {
        name: 'AI Research Scientist',
        jobRoles: ['AI Researcher', 'Algorithm Developer', 'PhD Researcher'],
        demand: '📈 Moderate',
        competition: '⚔️ Very High',
        skills: ['Python', 'Mathematics', 'Deep Learning', 'Research', 'Statistics'],
        tools: ['TensorFlow', 'PyTorch', 'Jupyter', 'LaTeX', 'Git'],
        timeTaken: '⏳ 24-36 months',
        roadmap: 'Math → Programming → ML → Research Methods → Publish Papers',
      },
      {
        name: 'Deep Learning Engineer',
        jobRoles: ['Neural Network Engineer', 'DL Specialist', 'AI Model Developer'],
        demand: '📈 High',
        competition: '⚔️ High',
        skills: ['Python', 'TensorFlow', 'PyTorch', 'CNNs', 'RNNs'],
        tools: ['TensorFlow', 'PyTorch', 'CUDA', 'Jupyter', 'AWS'],
        timeTaken: '⏳ 12-24 months',
        roadmap: 'Python → ML Basics → Neural Networks → Deep Learning → Optimization',
      },
      {
        name: 'Computer Vision Engineer',
        jobRoles: ['CV Developer', 'Image Processing Engineer', 'Vision AI Engineer'],
        demand: '📈 High',
        competition: '⚔️ High',
        skills: ['Python', 'OpenCV', 'Deep Learning', 'Image Processing', 'CNNs'],
        tools: ['OpenCV', 'TensorFlow', 'PyTorch', 'MATLAB', 'Git'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'Python → Image Processing → OpenCV → Deep Learning → CV Projects',
      },
      {
        name: 'NLP Engineer',
        jobRoles: ['NLP Developer', 'Chatbot Engineer', 'Text Mining Specialist'],
        demand: '📈 High',
        competition: '⚔️ High',
        skills: ['Python', 'NLP', 'Transformers', 'Spacy', 'Deep Learning'],
        tools: ['NLTK', 'Spacy', 'Hugging Face', 'TensorFlow', 'Git'],
        timeTaken: '⏳ 12-18 months',
        roadmap: 'Python → Text Processing → NLP Basics → Transformers → NLP Projects',
      },
    ],
  },
];

export default function CareerPath() {
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  return (
    <div className="min-h-screen bg-[#0A0F20] text-white font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-4">
            🎯 Explore Tech Career Paths
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Discover the right tech career for you. From software development to AI, explore roles, skills, and roadmaps to kickstart your journey.
          </p>
        </motion.div>

        {/* Motivational Section */}
        

        {/* Path Selection */}
        {!selectedPath ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {careerPaths.map((path, index) => (
              <motion.div
                key={index}
                className="bg-[#1A1F30] rounded-lg p-6 border border-gray-600 shadow-md hover:shadow-xl hover:border-blue-500 cursor-pointer flex items-center justify-between transition-colors"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelectedPath(path)}
              >
                <div className="flex items-center space-x-4">
                  {path.icon}
                  <h2 className="text-xl font-semibold text-gray-100">{path.title}</h2>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : !selectedField ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#1A1F30] p-8 rounded-xl shadow-lg border border-gray-600"
          >
            <button
              onClick={() => setSelectedPath(null)}
              className="bg-gray-600 text-gray-200 font-bold py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors mb-6"
            >
              🔙 Back
            </button>

            <h2 className="text-3xl font-bold text-blue-400 mb-6">{selectedPath.title}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedPath.specializations.map((field, index) => (
                <motion.div
                  key={index}
                  className="bg-[#121624] p-6 rounded-lg shadow-md cursor-pointer hover:bg-[#1f2937] hover:shadow-xl transition-all"
                  whileHover={{ scale: 1.03 }}
                  onClick={() => setSelectedField(field)}
                >
                  <h3 className="text-xl font-semibold text-gray-100">{field.name}</h3>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#1A1F30] shadow-lg rounded-xl border border-gray-600 p-8 max-w-5xl mx-auto"
          >
            <button
              onClick={() => setSelectedField(null)}
              className="bg-gray-600 text-gray-200 font-bold py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors mb-6"
            >
              🔙 Back
            </button>

            <h2 className="text-3xl font-bold text-blue-400 mb-6">{selectedField.name}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {['jobRoles', 'demand', 'competition', 'skills', 'tools', 'timeTaken', 'roadmap'].map((key, index) => (
                <div
                  key={index}
                  className="bg-[#121624] p-6 rounded-lg shadow-md border border-gray-600 hover:shadow-xl hover:border-blue-500 transition-all"
                >
                  <h3 className="text-lg font-semibold text-gray-100 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </h3>
                  <p className="text-gray-400 mt-2">
                    {Array.isArray(selectedField[key]) ? selectedField[key].join(', ') : selectedField[key]}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Floating Button */}
      {/* <div className="fixed bottom-6 right-6">
        <button className="bg-blue-600 text-white rounded-full p-3 shadow-lg hover:bg-blue-700 transition">
          <FaLaptopCode className="w-6 h-6" />
        </button>
      </div> */}

      {/* Uncomment if Footer exists */}
      {/* <Footer /> */}
    </div>
  );
}